import {
  useEffect,
  useState,
  type FormEvent,
} from "react";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  FileText,
  Images,
  LogOut,
  MenuSquare,
  MessageSquareText,
  ShoppingBag,
  ShieldCheck,
} from "lucide-react";

import { supabase } from "../lib/supabase";
import { NovaCmsNotificationBell } from "./NovaCmsNotificationBell";
import {
  NovaCmsEnquiries,
  NovaCmsReservations,
} from "./NovaCmsOperations";
import { NovaCmsFeedback } from "./NovaCmsFeedback";
import { NovaCmsWebsiteEditor } from "./NovaCmsWebsiteEditor";
import { NovaCmsMediaManager } from "./NovaCmsMediaManager";
import { NovaCmsMenuManager } from "./NovaCmsMenuManager";
import { NovaCmsOrders } from "./NovaCmsOrders";
import { NovaCmsPublishing } from "./NovaCmsPublishing";

type CmsAdmin = {
  display_name: string | null;
  role: string;
};

type CmsSessionState =
  | {
      status: "loading";
    }
  | {
      status: "signed-out";
    }
  | {
      status: "unauthorized";
      email: string | null;
    }
  | {
      status: "authorized";
      email: string | null;
      admin: CmsAdmin;
    };

type CmsModule = {
  title: string;
  description: string;
  icon: typeof FileText;
  status: "ready" | "next";
  href?: string;
};

const MODULES: CmsModule[] = [
  {
    title: "Website",
    description:
      "Edit every public heading, sentence, button, comma and full stop.",
    icon: FileText,
    status: "ready",
    href: "/nova-hq/website",
  },
  {
    title: "Menu",
    description:
      "Manage the single live menu used across the website and QR menu.",
    icon: MenuSquare,
    status: "ready",
    href: "/nova-hq/menu",
  },
  {
    title: "Orders",
    description:
      "Review website and QR orders, customer details, fulfilment and order contents.",
    icon: ShoppingBag,
    status: "ready",
    href: "/nova-hq/orders",
  },
  {
    title: "Media",
    description:
      "Upload, preview and publish Nova photography and website assets.",
    icon: Images,
    status: "ready",
    href: "/nova-hq/media",
  },
  {
    title: "Reservations",
    description:
      "Receive, review and manage table reservation requests.",
    icon: CalendarDays,
    status: "ready",
    href: "/nova-hq/reservations",
  },
  {
    title: "Service Enquiries",
    description:
      "Manage cakes, catering, corporate lunches, events and celebrations.",
    icon: MessageSquareText,
    status: "ready",
    href: "/nova-hq/enquiries",
  },
  {
    title: "Feedback",
    description:
      "Review private guest feedback, ratings and follow-up needs.",
    icon: MessageSquareText,
    status: "ready",
    href: "/nova-hq/feedback",
  },
  {
    title: "Publishing",
    description:
      "Review draft changes before publishing them to the live website.",
    icon: BookOpen,
    status: "ready",
    href: "/nova-hq/publishing",
  },
];

async function resolveCmsSession(): Promise<CmsSessionState> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    return {
      status: "signed-out",
    };
  }

  const { data, error } = await supabase.rpc(
    "get_my_nova_cms_access",
  );

  if (error || !data) {
    return {
      status: "unauthorized",
      email: session.user.email ?? null,
    };
  }

  const access = Array.isArray(data)
    ? data[0]
    : data;

  if (
    !access ||
    access.allowed !== true
  ) {
    return {
      status: "unauthorized",
      email: session.user.email ?? null,
    };
  }

  return {
    status: "authorized",
    email: session.user.email ?? null,
    admin: {
      display_name:
        access.display_name ?? null,
      role:
        access.role ?? "editor",
    },
  };
}

function LoginScreen({
  onAuthorized,
}: {
  onAuthorized: (
    state: CmsSessionState,
  ) => void;
}) {
  const [email, setEmail] =
    useState("");
  const [password, setPassword] =
    useState("");
  const [busy, setBusy] =
    useState(false);
  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    setBusy(true);
    setErrorMessage(null);

    const { error } =
      await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

    if (error) {
      setErrorMessage(
        "The email or password was not accepted.",
      );
      setBusy(false);
      return;
    }

    const nextState =
      await resolveCmsSession();

    if (
      nextState.status !==
      "authorized"
    ) {
      await supabase.auth.signOut();

      setErrorMessage(
        "This account does not have access to Nova HQ.",
      );
      setBusy(false);
      return;
    }

    onAuthorized(nextState);
  }

  return (
    <main className="min-h-screen bg-[#24392d] px-5 py-12 text-[#f3ead7] sm:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-6xl items-center justify-center">
        <div className="grid w-full overflow-hidden border border-[#d6bd91]/20 bg-[#314a3b] shadow-2xl lg:grid-cols-[1.05fr_.95fr]">
          <div className="relative hidden min-h-[680px] overflow-hidden border-r border-[#d6bd91]/15 p-12 lg:flex lg:flex-col lg:justify-between">
            <div className="absolute -right-36 -top-36 h-96 w-96 rounded-full border border-[#d6bd91]/15" />
            <div className="absolute -bottom-44 -left-44 h-[500px] w-[500px] rounded-full border border-[#d6bd91]/10" />

            <div className="relative">
              <img
                src="/images/nova/logodark.png"
                onError={(event) => {
                  event.currentTarget.src =
                    "/images/nova/novatransparent.png";
                }}
                alt="Nova Res Culina"
                className="h-auto w-[190px] object-contain"
              />
            </div>

            <div className="relative max-w-md">
              <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.26em] text-[#d6bd91]">
                Nova HQ
              </p>

              <h1 className="font-['Cormorant_Garamond'] text-6xl leading-[0.92]">
                The restaurant,
                <br />
                behind the scenes.
              </h1>

              <p className="mt-7 max-w-sm text-sm leading-7 text-[#d9d2c2]/75">
                Content, menu, media,
                reservations and guest
                enquiries — managed from
                one place.
              </p>
            </div>

            <p className="relative text-[9px] uppercase tracking-[0.2em] text-[#d9d2c2]/40">
              Nova Res Culina · Nairobi
            </p>
          </div>

          <div className="flex min-h-[620px] items-center bg-[#eee9dc] p-7 text-[#314a3b] sm:p-12 lg:p-14">
            <div className="mx-auto w-full max-w-sm">
              <div className="mb-10 lg:hidden">
                <img
                  src="/images/nova/logo2.png"
                  onError={(event) => {
                    event.currentTarget.src =
                      "/images/nova/novatransparent.png";
                  }}
                  alt="Nova Res Culina"
                  className="h-auto w-[150px]"
                />
              </div>

              <div className="mb-10">
                <div className="mb-5 flex h-10 w-10 items-center justify-center border border-[#9d725c] text-[#9d725c]">
                  <ShieldCheck
                    size={18}
                    strokeWidth={1.4}
                  />
                </div>

                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
                  Authorized access
                </p>

                <h2 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none">
                  Welcome to
                  <br />
                  Nova HQ.
                </h2>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-7"
              >
                <label className="block">
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                    Email address
                  </span>

                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) =>
                      setEmail(
                        event.target.value,
                      )
                    }
                    className="w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm outline-none transition-colors focus:border-[#9d725c]"
                  />
                </label>

                <label className="block">
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                    Password
                  </span>

                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) =>
                      setPassword(
                        event.target.value,
                      )
                    }
                    className="w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm outline-none transition-colors focus:border-[#9d725c]"
                  />
                </label>

                {errorMessage && (
                  <div className="border border-[#9d725c]/30 bg-[#9d725c]/8 px-4 py-3 text-xs leading-5 text-[#7d5544]">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="flex w-full items-center justify-between bg-[#314a3b] px-5 py-4 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] transition-colors hover:bg-[#9d725c] disabled:cursor-wait disabled:opacity-60"
                >
                  <span>
                    {busy
                      ? "Checking access…"
                      : "Enter Nova HQ"}
                  </span>

                  <ArrowRight
                    size={15}
                  />
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  window.location.href =
                    "/";
                }}
                className="mt-8 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#718076] underline decoration-[#b5bbae] underline-offset-4"
              >
                Return to website
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function UnauthorizedScreen({
  email,
}: {
  email: string | null;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#eee9dc] px-6 text-[#314a3b]">
      <div className="max-w-md text-center">
        <ShieldCheck
          className="mx-auto text-[#9d725c]"
          size={32}
          strokeWidth={1.2}
        />

        <p className="mt-7 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d725c]">
          Nova HQ
        </p>

        <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl">
          Access not granted.
        </h1>

        <p className="mt-5 text-sm leading-6 text-[#667067]">
          {email
            ? `${email} is authenticated, but is not currently an authorized Nova CMS account.`
            : "This account is not currently authorized for Nova HQ."}
        </p>

        <button
          type="button"
          onClick={async () => {
            await supabase.auth.signOut();
            window.location.reload();
          }}
          className="mt-8 bg-[#314a3b] px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7]"
        >
          Sign out
        </button>
      </div>
    </main>
  );
}

function Dashboard({
  email,
  admin,
}: {
  email: string | null;
  admin: CmsAdmin;
}) {
  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#b5bbae] bg-[#314a3b] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 py-5 sm:px-8 lg:px-10">
          <div className="flex items-center gap-5">
            <img
              src="/images/nova/logodark.png"
              onError={(event) => {
                event.currentTarget.src =
                  "/images/nova/novatransparent.png";
              }}
              alt="Nova Res Culina"
              className="h-14 w-auto object-contain sm:h-16"
            />

            <div className="hidden border-l border-[#f3ead7]/15 pl-5 sm:block">
              <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#d6bd91]">
                Nova HQ
              </p>

              <p className="mt-1 text-xs text-[#d9d2c2]/65">
                Content & operations
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden text-right md:block">
              <p className="text-xs">
                {admin.display_name ||
                  email ||
                  "Administrator"}
              </p>

              <p className="mt-1 text-[8px] uppercase tracking-[0.18em] text-[#d6bd91]">
                {admin.role}
              </p>
            </div>

            <NovaCmsNotificationBell />

          <button
              type="button"
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.reload();
              }}
              className="flex h-10 items-center gap-2 border border-[#f3ead7]/20 px-3 text-[9px] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-[#d6bd91] hover:text-[#d6bd91]"
            >
              <LogOut size={13} />
              <span className="hidden sm:inline">
                Sign out
              </span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-10 lg:py-14">
        <div className="max-w-3xl">
          <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
            Nova Res Culina
          </p>

          <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-[0.95] sm:text-6xl">
            Everything Nova publishes,
            <br />
            from one place.
          </h1>

          <p className="mt-6 max-w-2xl text-sm leading-7 text-[#667067]">
            This is the production CMS
            foundation. Website copy,
            imagery, menu content and
            operational enquiries will
            progressively move here without
            redesigning the approved public
            experience.
          </p>
        </div>

        <section className="mt-12 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {MODULES.map((module) => {
            const Icon = module.icon;

            return (
              <article
                key={module.title}
                onClick={() => {
                  if (module.href) {
                    window.location.href =
                      module.href;
                  }
                }}
                className={[
                  "group min-h-[220px] border border-[#b5bbae] bg-[#f4efe4] p-6 transition-transform hover:-translate-y-1",
                  module.href
                    ? "cursor-pointer"
                    : "",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-5">
                  <span className="flex h-10 w-10 items-center justify-center border border-[#9d725c]/35 text-[#9d725c]">
                    <Icon
                      size={17}
                      strokeWidth={1.4}
                    />
                  </span>

                  <span className="text-[8px] font-semibold uppercase tracking-[0.18em] text-[#8d988d]">
                    {module.status ===
                    "ready"
                      ? "Foundation ready"
                      : "Next build"}
                  </span>
                </div>

                <h2 className="mt-8 font-['Cormorant_Garamond'] text-3xl">
                  {module.title}
                </h2>

                <p className="mt-3 max-w-sm text-xs leading-6 text-[#667067]">
                  {module.description}
                </p>
              </article>
            );
          })}
        </section>

        <div className="mt-12 flex flex-col justify-between gap-6 border-t border-[#b5bbae] pt-7 text-[9px] uppercase tracking-[0.18em] text-[#8d988d] sm:flex-row">
          <span>
            Nova CMS · Production foundation
          </span>

          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            className="text-left text-[#314a3b] underline decoration-[#9d725c] underline-offset-4"
          >
            View live website
          </button>
        </div>
      </div>
    </main>
  );
}

export function NovaCmsApp() {
  const [sessionState, setSessionState] =
    useState<CmsSessionState>({
      status: "loading",
    });

  useEffect(() => {
    let active = true;

    void resolveCmsSession().then(
      (nextState) => {
        if (active) {
          setSessionState(
            nextState,
          );
        }
      },
    );

    const {
      data: { subscription },
    } =
      supabase.auth.onAuthStateChange(
        () => {
          window.setTimeout(() => {
            void resolveCmsSession().then(
              (nextState) => {
                if (active) {
                  setSessionState(
                    nextState,
                  );
                }
              },
            );
          }, 0);
        },
      );

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  if (
    sessionState.status ===
    "loading"
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#314a3b] text-[#f3ead7]">
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#d6bd91]">
          Opening Nova HQ…
        </p>
      </main>
    );
  }

  if (
    sessionState.status ===
    "signed-out"
  ) {
    return (
      <LoginScreen
        onAuthorized={
          setSessionState
        }
      />
    );
  }

  if (
    sessionState.status ===
    "unauthorized"
  ) {
    return (
      <UnauthorizedScreen
        email={
          sessionState.email
        }
      />
    );
  }

  const pathname =
    window.location.pathname;

  if (
    window.location.pathname ===
    "/nova-hq/orders"
  ) {
    return <NovaCmsOrders />;
  }


  if (
    pathname ===
    "/nova-hq/website"
  ) {
    return <NovaCmsWebsiteEditor />;
  }

  if (
    pathname ===
    "/nova-hq/menu"
  ) {
    return <NovaCmsMenuManager />;
  }

  if (
    pathname ===
    "/nova-hq/media"
  ) {
    return <NovaCmsMediaManager />;
  }

  if (
    pathname ===
    "/nova-hq/publishing"
  ) {
    return <NovaCmsPublishing />;
  }

  if (
    pathname ===
    "/nova-hq/reservations"
  ) {
    return <NovaCmsReservations />;
  }

  if (
    pathname ===
    "/nova-hq/enquiries"
  ) {
    return <NovaCmsEnquiries />;
  }

  if (
    pathname ===
    "/nova-hq/feedback"
  ) {
    return <NovaCmsFeedback />;
  }

  return (
    <Dashboard
      email={sessionState.email}
      admin={sessionState.admin}
    />
  );
}
